import { useQuery } from '@tanstack/react-query'

import { Screen, ScreenHeader } from '../components/chrome/shell'
import { Panel } from '../components/ui/panel'
import { Chip, type Tone } from '../components/ui/tone'
import { api } from '../lib/api'

/**
 * Alerts (Phase 8, item 52).
 *
 * System problems that need attention: failed sends, rate limit pressure, mailbox health,
 * credential expiry, quota exhaustion.
 */

interface Alert {
  id: string
  severity: 'critical' | 'warning' | 'info'
  title: string
  description: string
  mailbox?: string
  action?: string
  createdAt: string
}

interface AlertsData {
  total: number
  critical: number
  warnings: number
  alerts: Alert[]
}

const SEVERITY_TONE: Record<Alert['severity'], Tone> = {
  critical: 'bad',
  warning: 'warn',
  info: 'info',
}

export function AlertsScreen() {
  const alerts = useQuery({
    queryKey: ['alerts'],
    queryFn: () => api.get<AlertsData>('/system/alerts'),
    refetchInterval: 30000, // Poll every 30 seconds
  })

  const data = alerts.data

  return (
    <Screen>
      <ScreenHeader
        title="Alerts"
        description="Problems requiring attention"
      />

      <div className="space-y-4">
        {/* Summary */}
        {data && (
          <Panel title="Summary" description={`${data.total} total alert${data.total !== 1 ? 's' : ''}`}>
            <div className="flex gap-4 px-4 py-3">
              {data.critical > 0 && (
                <div className="flex items-center gap-2">
                  <Chip tone="bad">{data.critical}</Chip>
                  <span className="text-muted text-[13px]">
                    Critical {data.critical === 1 ? 'issue' : 'issues'}
                  </span>
                </div>
              )}
              {data.warnings > 0 && (
                <div className="flex items-center gap-2">
                  <Chip tone="warn">{data.warnings}</Chip>
                  <span className="text-muted text-[13px]">
                    {data.warnings === 1 ? 'Warning' : 'Warnings'}
                  </span>
                </div>
              )}
              {data.total === 0 && (
                <span className="text-muted text-[13px]">All systems operational</span>
              )}
            </div>
          </Panel>
        )}

        {/* Alerts List */}
        <Panel
          title="Active alerts"
          description="Sorted by severity and age"
        >
          {!data ? (
            <div className="px-4 py-3 text-muted text-[12.5px]">Loading…</div>
          ) : data.alerts.length === 0 ? (
            <div className="px-4 py-3 text-muted text-[12.5px]">No active alerts</div>
          ) : (
            <div className="divide-line2 divide-y">
              {data.alerts.map((alert) => (
                <div
                  key={alert.id}
                  className="flex items-start justify-between gap-4 px-4 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-2">
                      <h4 className="font-medium text-[13px] text-text flex-1">{alert.title}</h4>
                      <Chip tone={SEVERITY_TONE[alert.severity]}>
                        {alert.severity === 'critical'
                          ? 'Critical'
                          : alert.severity === 'warning'
                            ? 'Warning'
                            : 'Info'}
                      </Chip>
                    </div>
                    <p className="text-muted mt-1 text-[12px]">{alert.description}</p>
                    {alert.mailbox && (
                      <p className="text-faint mt-1 text-[11.5px]">
                        Mailbox: <span className="font-mono">{alert.mailbox}</span>
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-faint text-[11px] tabular-nums">
                        {new Date(alert.createdAt).toLocaleString()}
                      </span>
                      {alert.action && (
                        <span className="text-accent text-[11px]">{alert.action}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        {/* Common Alert Types */}
        <Panel
          title="What triggers alerts"
          description="Automatic detection"
        >
          <div className="space-y-2 px-4 py-3 text-[12.5px]">
            <div className="flex items-start gap-2">
              <span className="text-bad">●</span>
              <div>
                <p className="font-medium text-text">Failed sends</p>
                <p className="text-muted">5+ messages failed in the last hour</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-warn">●</span>
              <div>
                <p className="font-medium text-text">Rate limit approaching</p>
                <p className="text-muted">Usage &gt; 80% of daily mailbox limit</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-warn">●</span>
              <div>
                <p className="font-medium text-text">Credential expiring</p>
                <p className="text-muted">OAuth token expires in &lt; 7 days</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-bad">●</span>
              <div>
                <p className="font-medium text-text">Mailbox unhealthy</p>
                <p className="text-muted">Health check failed or connection lost</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-warn">●</span>
              <div>
                <p className="font-medium text-text">Database latency</p>
                <p className="text-muted">p99 latency &gt; 500ms</p>
              </div>
            </div>
          </div>
        </Panel>
      </div>
    </Screen>
  )
}
